import { useGetCustomGeoJsonByUserFileId } from "@/api/hooks/customGeojson";
import { dataProcessingThreadPool } from "@/threads";
import type { ParsedPoiListResponse, PoiDataGrid } from "@/threads/DataProcessingThread/types";
import type { LabeledCoordinates } from "@/types/location";
import type { ColDef, ValueSetterParams } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import type { GeoJSON } from "geojson";
import { isEmpty, omit } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PoiColumnHeader } from "./PoiColumnHeader";
import {
  addPropertyToData,
  coordColDef,
  dataColDef,
  exportColumnsFromData,
  groupColumns,
  replaceKeyInObject,
} from "./utils";

function createDedupeRegexForColumnName(name: string) {
  return new RegExp(`^${name}(?: \$begin:math:text$(\\\\d+)\\$end:math:text$)?$`);
}

// Custom hook to manage POI table state and handlers
export function usePoiTableHandlers({
  initialData,
  onChange,
  tableRef,
}: {
  initialData: PoiDataGrid;
  onChange: (data: PoiDataGrid, isPolygon?: boolean) => void;
  tableRef: React.RefObject<AgGridReact>;
}) {
  const [data, setData] = useState<PoiDataGrid>(initialData || []);
  const [rowsSelected, setRowsSelected] = useState(false);

  const dataRef = useRef<{ [column: string]: any }[]>([]);
  dataRef.current = data;

  const gridApi = tableRef.current?.api;
  const [userFileId, setUserFileId] = useState<number | null>(null);
  const [geojson, setGeojson] = useState<PoiDataGrid | null>(null);
  const query = useGetCustomGeoJsonByUserFileId(userFileId);
  const isLoading = query?.isLoading ?? false;
  const error = query?.error ?? null;

  useEffect(() => {
    if (userFileId !== null && query.geojson) {
      const parsed = parseGeoJsonProperties(query.geojson);
      setGeojson(parsed);
      setData(parsed);
      onChange(parsed);
      setTimeout(() => {
        gridApi?.autoSizeAllColumns();
      });
    }
  }, [query.geojson, userFileId, onChange, gridApi]);

  const handleAddProperty = useCallback(() => {
    const propertyIndex = colDefs.length - 2;
    const propertyName = `property${propertyIndex ? `_${propertyIndex}` : ""}`;

    const regex = createDedupeRegexForColumnName(propertyName);
    const duplicateColumnCount = colDefs.filter((colDef) => regex.test(colDef.field || "")).length;

    const dedupedPropertyName =
      duplicateColumnCount > 0 ? `${propertyName} (${duplicateColumnCount + 1})` : propertyName;

    const updatedData = addPropertyToData(data, dedupedPropertyName);
    setData(updatedData);
    onChange(updatedData);

    setTimeout(() => {
      gridApi?.ensureColumnVisible(propertyName);
    });
  }, [data, gridApi, onChange]);

  const handleRemoveProperty = useCallback(
    (colId: string) => {
      const rowsData = dataRef.current;
      const filteredData = rowsData.map((row) => omit(row, [colId]));
      setData(filteredData);
      onChange(filteredData);
      setTimeout(() => {
        gridApi?.autoSizeAllColumns();
      });
    },
    [gridApi, onChange],
  );
  const handleExportCSV = useCallback(() => {
    if (!tableRef.current) {
      return;
    }
    tableRef.current?.api.exportDataAsCsv({
      fileName: "Locations",
    });
  }, [tableRef.current?.api]);

  const handleColumnNameChange = useCallback(
    (newName: string, oldName: string) => {
      const rowsData = dataRef.current;
      const updatedRowsData = rowsData.map((row) => {
        const regex = createDedupeRegexForColumnName(newName);
        const duplicateColumnCount = Object.keys(row).filter(
          (columnName) => oldName !== columnName && regex.test(columnName),
        ).length;
        const dedupedName = duplicateColumnCount > 0 ? `${newName} (${duplicateColumnCount + 1})` : newName;
        return replaceKeyInObject(row, oldName, dedupedName, row[oldName]);
      });
      setData(updatedRowsData);
      onChange(updatedRowsData);
      setTimeout(() => {
        gridApi?.autoSizeAllColumns();
      });
    },
    [gridApi, onChange],
  );

  const handleCellValueSetter = useCallback(
    (params: ValueSetterParams) => {
      if (params.node && params.node.rowIndex != null) {
        const { rowIndex } = params.node;
        const updatedRowsData = [...data];
        const updatableRow = updatedRowsData[rowIndex];
        const updatedColumnId = params.column.getColId();
        const newValue = params.newValue;
        updatedRowsData[rowIndex] = {
          ...updatableRow,
          [updatedColumnId]: newValue,
        };
        setData(updatedRowsData);
        onChange(updatedRowsData);
        setTimeout(() => {
          gridApi?.ensureIndexVisible(rowIndex);
        });
      }
      return false;
    },
    [data, gridApi, onChange],
  );

  const colDefs = useMemo<ColDef[]>(() => {
    if (!data.length) return [];

    const checkboxColumns: ColDef = {
      checkboxSelection: true,
      lockPosition: true,
      resizable: false,
      editable: false,
      sortable: false,
      pinned: true,
      maxWidth: 34,
    };

    const columns = exportColumnsFromData(data);
    const { coordCols, dataCols } = groupColumns(columns);

    const coordColumns = coordCols.map((column) => ({
      ...coordColDef(column),
      valueSetter: handleCellValueSetter,
    }));
    const dataColumns = dataCols.map((column) => ({
      ...dataColDef(column),
      valueSetter: handleCellValueSetter,
      headerComponent: PoiColumnHeader,
      headerComponentParams: {
        onRemoveColumn: handleRemoveProperty,
        onColumnNameChange: handleColumnNameChange,
      },
    }));

    return [checkboxColumns, ...coordColumns, ...dataColumns];
  }, [data, handleCellValueSetter, handleRemoveProperty, handleColumnNameChange]);

  const handleCsvUploaded = (poiList: ParsedPoiListResponse) => {
    setData(poiList.data);
    onChange(poiList.data);
    setTimeout(() => {
      gridApi?.autoSizeAllColumns();
    });
  };

  const handleGeojsonSelected = (poiList: ParsedPoiListResponse) => {
    setData(poiList.data);
    onChange(poiList.data);
    setTimeout(() => {
      gridApi?.autoSizeAllColumns();
    });
  };

  const handleAddPoint = (coord: LabeledCoordinates) => {
    const addedRowIndex = data.length;
    const updatedData = coord.label
      ? data.concat({ lat: coord.lat, lon: coord.lon, label: coord.label })
      : data.concat({ lat: coord.lat, lon: coord.lon });
    setData(updatedData);
    onChange(updatedData);
    setTimeout(() => {
      gridApi?.autoSizeAllColumns();
      gridApi?.ensureIndexVisible(addedRowIndex);
    });
  };

  const handleSelectRows = () => {
    const api = gridApi;
    setRowsSelected(!isEmpty(api?.getSelectedRows()));
  };

  const handleRemoveRows = () => {
    if (gridApi) {
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedIndex = selectedNodes.map((node) => node.rowIndex);
      const updatedData = data.filter((_row, index) => !selectedIndex.includes(index));
      setData(updatedData);
      onChange(updatedData);
    }
  };

  const handleCsvFileDrop = (files: FileList) => {
    dataProcessingThreadPool.parseCsvFiles(files).then((response) => {
      handleCsvUploaded(response);
    });
  };

  return {
    data,
    rowsSelected,
    colDefs,
    handleAddProperty,
    handleRemoveProperty,
    handleColumnNameChange,
    handleCellValueSetter,
    handleCsvUploaded,
    handleGeojsonSelected,
    handleAddPoint,
    handleSelectRows,
    handleRemoveRows,
    handleCsvFileDrop,
    handleExportCSV,
    geojson,
    isLoading,
    error,
    userFileId,
    setUserFileId,
  };
}

export function parseGeoJsonProperties(geojson: GeoJSON): PoiDataGrid {
  if (geojson.type !== "FeatureCollection" || geojson.features?.[0].geometry?.type !== "Polygon") {
    return [];
  }
  return (
    geojson.features?.map((feature: any) => {
      const props = feature?.properties || {};
      const polygon = feature?.geometry?.coordinates?.[0].toString();
      //TODO polygon format is lat,lon,lat,lon,lat,lon...
      const { lat, lon, name, ...rest } = props;
      return {
        lat,
        lon,
        name,
        ...rest,
        polygon,
      };
    }) ?? []
  );
}
